import { faroCoreApiClient } from "@api/faro-core-api-client";
import { SPACE_ELEMENTS_OF_MODAL } from "@components/common/dialog/faro-dialog";
import { GroupAutoComplete } from "@components/common/group-autocomplete/group-autocomplete";
import { LabelWithHelp } from "@components/common/label-with-help";
import {
  MemberAutocomplete,
  SelectedMember,
} from "@components/common/member-autocomplete/member-autocomplete";
import { useErrorContext } from "@context-providers/error-boundary/error-handling-context";
import { BaseCompanyIdProps, GroupId } from "@custom-types/base-types";
import { ReactSetStateFunction } from "@custom-types/types";
import { Stack } from "@mui/material";
import { FaroCoreApiTypes } from "@stellar/core-api-client";
import { useEffect, useState } from "react";

interface Props extends BaseCompanyIdProps {
  /** The selected group ID */
  selectedGroupId: GroupId | undefined;

  /** Setter for selectedGroupId */
  setSelectedGroupId: ReactSetStateFunction<GroupId | undefined>;

  /** The selected project manager */
  selectedProjectManager: SelectedMember;

  /** Setter for setSelectedProjectManager */
  setSelectedProjectManager: ReactSetStateFunction<SelectedMember>;
}

/** List of groups and project managers with selecting ability */
export function GroupAndProjectManagerSelection({
  selectedGroupId,
  setSelectedGroupId,
  selectedProjectManager,
  setSelectedProjectManager,
  companyId,
}: Props): JSX.Element {
  const { handleErrorWithToast } = useErrorContext();

  const [groupMembers, setGroupMembers] = useState<
    FaroCoreApiTypes.GroupMember[]
  >([]);

  // Triggered every time a group is selected.
  // It fetches the members that belong to that group and stores them
  // in the members variable.
  useEffect(() => {
    async function onGroupSelected(
      groupId: GroupId | undefined
    ): Promise<void> {
      if (!groupId) {
        return undefined;
      }

      // As soon as you select a different group, empty the members array.
      // TODO: This should be moved to the group slice as createAsyncThunk: https://faro01.atlassian.net/browse/ST-698
      setGroupMembers([]);
      try {
        const { members } = await faroCoreApiClient.groups.getGroupById({
          companyId,
          groupId,
        });

        const membersResult = members ?? [];
        setGroupMembers(membersResult);
      } catch (error) {
        handleErrorWithToast({
          id: `getGroupById-${Date.now().toString()}`,
          title: "Cannot get the group details",
          error,
        });
      }
    }

    void onGroupSelected(selectedGroupId);
  }, [companyId, handleErrorWithToast, selectedGroupId]);

  return (
    <>
      <Stack sx={{ mb: SPACE_ELEMENTS_OF_MODAL }}>
        <LabelWithHelp title="Group" />
        <GroupAutoComplete
          callbackOnGroupSelected={setSelectedGroupId}
          selectedGroupId={selectedGroupId}
          placeholder="Please select a group..."
        />
      </Stack>
      <Stack sx={{ mb: SPACE_ELEMENTS_OF_MODAL }}>
        <LabelWithHelp title="Manager" isDisabled={!selectedGroupId} />
        <MemberAutocomplete
          members={groupMembers}
          selectedMember={selectedProjectManager}
          onOptionChange={setSelectedProjectManager}
          isDisabled={!selectedGroupId}
          placeholder="Choose who can manage this project..."
        />
      </Stack>
    </>
  );
}
